import React from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import {
  BookOpen,
  Smartphone,
  Star,
  Radio,
  Info,
  Mail,
  Globe,
  Phone,
  ExternalLink,
  Video,
  Monitor,
  Chrome,
  Server,
  ArrowRight
} from "lucide-react";

export default function Instructions() {
  const { t } = useTranslation();

  const steps = [
    {
      icon: <Smartphone size={22} />,
      title: t("instructionsPage.step1Title", "Download the app"),
      body: t("instructionsPage.step1Body", "Visit conferencecaptioning.com/app and download the app on a phone, tablet or Mac, then open it."),
    },
    {
      icon: <Star size={22} />,
      title: t("instructionsPage.step2Title", "Activate your Premium account"),
      body: t("instructionsPage.step2Body", "In the app, enter as an Organizer, not an Attendee, and tap the info button. Tap Activate, sign in on the web, choose your stream, then open the app when asked."),
    },
    {
      icon: <Radio size={22} />,
      title: t("instructionsPage.step3Title", "Start streaming"),
      body: t("instructionsPage.step3Body", "Once Premium is active, tap Start Premium Stream. Share the stream link or QR code with your attendees."),
    },
  ];

  const resources = [
    {
      title: t("instructionsPage.resProPresenterTitle", "How to Add Live Captions & Translations to ProPresenter"),
      description: t("instructionsPage.resProPresenterText", "Integrate ConferenceCaptioning.com into advanced video mixing software for professional event production."),
      link: "https://conferencecaptioning.tawk.help/article/how-to-add-live-captions-translations-to-propresenter",
      icon: <Video className="text-blue-600" size={24} />,
      color: "bg-blue-50"
    },
    {
      title: t("instructionsPage.resSlidesTitle", "Live Captions & Translations For Any Slide Deck Presentation"),
      description: t("instructionsPage.resSlidesText", "Use \"Always On-Top\" mode to display real-time captions directly over PowerPoint, Keynote, or Google Slides."),
      link: "https://conferencecaptioning.tawk.help/article/live-captions-translations-slide-deck-presentation",
      icon: <Monitor className="text-indigo-600" size={24} />,
      color: "bg-indigo-50"
    },
    {
      title: t("instructionsPage.resChromeTitle", "Conference Captioning Chrome Extension (On-Device AI)"),
      description: t("instructionsPage.resChromeText", "Leverage the power of local AI on Windows or Mac using our specialized Chrome Extension for events."),
      link: "https://conferencecaptioning.tawk.help/article/conference-captioning-chrome-extension-on-device-ai",
      icon: <Chrome className="text-orange-600" size={24} />,
      color: "bg-orange-50"
    },
    {
      title: t("instructionsPage.resServerTitle", "How to Build a Local Translation Server on Mac Mini"),
      description: t("instructionsPage.resServerText", "A comprehensive guide to setting up a private, low-latency translation server for educational institutions."),
      link: "https://conferencecaptioning.tawk.help/article/setting-up-a-local-language-translation-server-on-a-mac",
      icon: <Server className="text-emerald-600" size={24} />,
      color: "bg-emerald-50"
    }
  ];

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="instructions" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-5xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">

            <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                  {t("navigation.instructions")}
                </h1>
                <p className="text-slate-500 mt-1 text-lg">
                  {t("instructionsPage.subtitle", "Everything you need to know about setting up and using ConfCap.")}
                </p>
              </div>
              {/* <div className="flex gap-3">
                <button className="btn-secondary flex items-center gap-2">
                  <Download size={18} />
                  Download PDF
                </button>
                <button className="btn-primary flex items-center gap-2">
                  <Maximize2 size={18} />
                  Fullscreen
                </button>
              </div> */}
            </header>

            {/* Setup guide, written in code so it can be edited and translated */}
            <section className="space-y-6" aria-labelledby="setup-title">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  <BookOpen size={20} />
                </div>
                <h2 id="setup-title" className="text-2xl font-bold text-slate-900">{t("instructionsPage.setupTitle", "Setup Instructions")}</h2>
              </div>
              <div className="bg-white rounded-[3rem] p-8 md:p-12 shadow-sm border border-slate-100">
                <ol className="grid md:grid-cols-3 gap-8">
                  {steps.map((step, index) => (
                    <li key={index} className="space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center" aria-hidden="true">{index + 1}</span>
                        <span className="text-blue-600">{step.icon}</span>
                      </div>
                      <h3 className="font-bold text-slate-900 text-lg">{step.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{step.body}</p>
                    </li>
                  ))}
                </ol>

                <div className="mt-10 flex items-start gap-3 rounded-2xl bg-indigo-50 text-indigo-900 p-4 text-sm">
                  <Info size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <p>{t("instructionsPage.tipAutoLock", "For long streams, go to Settings > Display & Brightness > Auto-Lock and choose Never, so the screen does not lock mid-event.")}</p>
                </div>

                <div className="mt-8 border-t border-slate-100 pt-8 grid sm:grid-cols-3 gap-4 text-sm text-slate-600">
                  <a className="flex items-center gap-2 hover:text-blue-600" href="mailto:hi@deafassistant.com"><Mail size={16} aria-hidden="true" /> hi@deafassistant.com</a>
                  <a className="flex items-center gap-2 hover:text-blue-600" href="https://conferencecaptioning.com" target="_blank" rel="noopener noreferrer"><Globe size={16} aria-hidden="true" /> conferencecaptioning.com</a>
                  <span className="flex items-center gap-2"><Phone size={16} aria-hidden="true" /> <span dir="ltr">+1 214-683-9508 / 226-526-8034</span></span>
                </div>
              </div>
            </section>

            {/* Advanced Resources */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                  <ExternalLink size={20} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{t("instructionsPage.advancedTitle", "Advanced Setup & Integrations")}</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-blue-100 transition-all duration-300 flex flex-col justify-between"
                  >
                    <div className="space-y-4">
                      <div className={`w-14 h-14 ${resource.color} rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-500`}>
                        {resource.icon}
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed">
                        {resource.description}
                      </p>
                    </div>
                    <div className="mt-8 flex items-center justify-between text-blue-600 font-bold text-sm">
                      <span className="flex items-center gap-1">
                        {t("instructionsPage.readGuide", "Read Guide")}
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                      <ExternalLink size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <footer className="bg-slate-900 rounded-[2rem] p-8 text-center text-white relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="text-xl font-bold mb-2">{t("instructionsPage.helpTitle", "Still need help?")}</h3>
                <p className="text-slate-400 mb-6">{t("instructionsPage.helpText", "Our support team is available to assist with your technical setup, just tap on the chat icon in the bottom right corner to get in touch.")}</p>
                {/* <button className="bg-white text-slate-900 px-8 py-3 rounded-2xl font-bold hover:bg-blue-50 transition-colors">
                  Contact Support
                </button> */}
              </div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
}
